import { Box, Group, Text, type FloatingPosition, rem } from '@mantine/core';
import type { EmotionSx } from '@mantine/emotion';
import { IconButton } from '@vrplatform/components';
import { THEME_COLORS } from '@vrplatform/ui-common';
import type { ReactNode } from 'react';
import { useId } from 'react';
import { VRPilotBadge } from './VRPilotBadge';

type VRPilotCoachmarkProps = {
  // Buttons shown below the text, such as "Got it" and "Next step".
  actions?: ReactNode;
  children: ReactNode;
  onClose?: () => void;
  // Draws the arrow on this side. Without it the coachmark has no arrow, as
  // in a popover that draws its own.
  placement?: FloatingPosition;
  sx?: EmotionSx;
  title: ReactNode;
};

const getArrowSx = (
  placement: FloatingPosition,
  layer: 'border' | 'surface'
) => {
  const isBorder = layer === 'border';
  const side = placement.split('-')[0];
  const align = placement.split('-')[1];
  const base = {
    background: isBorder ? THEME_COLORS.violet[6] : THEME_COLORS.neutral[0],
    content: '""',
    height: rem(isBorder ? 12 : 10),
    pointerEvents: 'none' as const,
    position: 'absolute' as const,
    width: rem(isBorder ? 8 : 6),
  };

  if (side === 'left') {
    return {
      ...base,
      clipPath: 'polygon(0 0, 100% 50%, 0 100%)',
      ...(align === 'end'
        ? { bottom: rem(isBorder ? 18 : 19) }
        : { top: rem(isBorder ? 18 : 19) }),
      right: rem(isBorder ? -8 : -6),
    };
  }

  if (side === 'right') {
    return {
      ...base,
      clipPath: 'polygon(0 50%, 100% 0, 100% 100%)',
      left: rem(isBorder ? -8 : -6),
      ...(align === 'end'
        ? { bottom: rem(isBorder ? 18 : 19) }
        : { top: rem(isBorder ? 18 : 19) }),
    };
  }

  if (side === 'top') {
    return {
      ...base,
      bottom: rem(isBorder ? -8 : -6),
      clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
      height: rem(isBorder ? 8 : 6),
      ...(align === 'end'
        ? { right: rem(isBorder ? 18 : 19) }
        : { left: rem(isBorder ? 18 : 19) }),
      width: rem(isBorder ? 12 : 10),
    };
  }

  return {
    ...base,
    clipPath: 'polygon(50% 0, 0 100%, 100% 100%)',
    height: rem(isBorder ? 8 : 6),
    ...(align === 'end'
      ? { right: rem(isBorder ? 18 : 19) }
      : { left: rem(isBorder ? 18 : 19) }),
    top: rem(isBorder ? -8 : -6),
    width: rem(isBorder ? 12 : 10),
  };
};

export const VRPilotCoachmark = ({
  actions,
  children,
  onClose,
  placement,
  sx,
  title,
}: VRPilotCoachmarkProps) => {
  const titleId = useId();
  const bodyId = useId();

  return (
    <Box
      aria-describedby={bodyId}
      aria-labelledby={titleId}
      data-placement={placement}
      role="dialog"
      sx={(theme, utils) => ({
        background: theme.colors.neutral[0],
        border: `${rem(1)} solid ${theme.colors.violet[6]}`,
        borderRadius: rem(8),
        boxShadow: `0 8px 18px color-mix(in srgb, ${theme.colors.neutral[9]} 8%, transparent)`,
        color: theme.colors.neutral[7],
        maxWidth: `min(${rem(280)}, calc(100vw - ${rem(32)}))`,
        minWidth: 0,
        padding: `${rem(10)} ${rem(12)}`,
        position: 'relative',
        width: 'max-content',
        ...(placement
          ? {
              '&::after': getArrowSx(placement, 'surface'),
              '&::before': getArrowSx(placement, 'border'),
            }
          : {}),
        ...(typeof sx === 'function' ? sx(theme, utils) : sx),
      })}
    >
      <Group align="flex-start" gap="xs" wrap="nowrap">
        <VRPilotBadge size="sm" sx={{ flex: '0 0 auto', marginTop: rem(1) }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Text
            c="violet.8"
            fw={600}
            id={titleId}
            size="xs"
            sx={{ overflowWrap: 'anywhere', paddingRight: onClose ? 18 : 0 }}
          >
            {title}
          </Text>
          <Text
            c="neutral.7"
            id={bodyId}
            mt={3}
            size="xs"
            sx={{ lineHeight: 1.25, overflowWrap: 'anywhere' }}
          >
            {children}
          </Text>
          {actions ? (
            <Group gap={8} justify="space-between" mt={10} wrap="nowrap">
              {actions}
            </Group>
          ) : null}
        </Box>
        {onClose ? (
          <IconButton
            aria-label="Dismiss VRPilot coachmark"
            color={THEME_COLORS.neutral[5]}
            icon="CrossIcon"
            iconSize={12}
            onClick={onClose}
            size={18}
            sx={{
              color: THEME_COLORS.neutral[5],
              position: 'absolute',
              right: 0,
              top: 0,
              ':hover': {
                color: THEME_COLORS.neutral[9],
              },
              ':hover svg path': {
                stroke: THEME_COLORS.neutral[9],
              },
            }}
          />
        ) : null}
      </Group>
    </Box>
  );
};
